import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { aiDraftMessages, samplePlan, samplePlanItems, getPlanBundle } from './planData';

const PlanItemPage = () => {
  const { planId, itemType, itemId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [discussionInput, setDiscussionInput] = useState('');
  const [discussionMessages, setDiscussionMessages] = useState([]);

  const currentBundle = useMemo(() => {
    return getPlanBundle(planId || samplePlan.id);
  }, [planId]);

  const currentPlan = currentBundle?.plan || samplePlan;
  const currentItems = currentBundle?.items || samplePlanItems;

  const item = useMemo(() => {
    return currentItems.find((entry) => entry.id === itemId) || {
      id: itemId,
      type: itemType,
      name: `${itemType} item`,
      createdBy: 'Patient (AI-assisted Wizard)',
      managedBy: 'Clinical Trial Management Team',
      approvalStatus: 'Pending',
      parentType: 'plan',
      parentId: planId || currentPlan.id,
      participants: ['Patient', 'Clinical Trial Management Team'],
      details: {}
    };
  }, [itemId, itemType, planId, currentItems, currentPlan.id]);

  const handlePostDiscussion = () => {
    const text = discussionInput.trim();
    if (!text) {
      return;
    }
    setDiscussionMessages((prev) => [...prev, { id: Date.now().toString(), from: 'Patient', to: 'Participants', text }]);
    setDiscussionInput('');
  };

  const detailEntries = Object.entries(item.details || {});

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto' }}>
      <Button variant="text" sx={{ mb: 2, fontWeight: 600 }} onClick={() => navigate(`/plans/${planId || currentPlan.id || samplePlan.id}`)}>
        ← Back to Plan
      </Button>

      <Card sx={{ mb: 3, borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>{item.name}</Typography>
          <Chip label={`Type: ${item.type}`} color="primary" sx={{ textTransform: 'capitalize', mb: 1.5 }} />
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Created by: {item.createdBy}</Typography></Grid>
            <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Managed by: {item.managedBy}</Typography></Grid>
            <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Approval status: {item.approvalStatus}</Typography></Grid>
            <Grid item xs={12} md={6}><Typography variant="body2" color="text.secondary">Parent: {item.parentType} ({item.parentId})</Typography></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            <Tab label="Discussion" />
            <Tab label="Participants" />
            <Tab label="Plan Item Type Details" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>AI-generated draft messages (editable)</Typography>
              <List dense sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                {aiDraftMessages.map((msg, index) => (
                  <ListItem key={`draft-${index}`}>
                    <ListItemText primary={msg} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Post discussion message"
                value={discussionInput}
                onChange={(e) => setDiscussionInput(e.target.value)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button variant="contained" onClick={handlePostDiscussion} disabled={!discussionInput.trim()}>Post</Button>
              </Box>
              <List sx={{ mt: 2 }}>
                {discussionMessages.map((m) => (
                  <ListItem key={m.id} sx={{ px: 0 }}>
                    <ListItemText primary={`${m.from} → ${m.to}`} secondary={m.text} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {activeTab === 1 && (
            <FormGroup>
              {item.participants.map((participant) => (
                <FormControlLabel key={participant} control={<Checkbox checked />} label={participant} />
              ))}
            </FormGroup>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Plan item type details for {item.type}.
              </Typography>
              {detailEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No specific details available for this item yet.</Typography>
              ) : (
                <List>
                  {detailEntries.map(([key, value]) => (
                    <ListItem key={key} sx={{ px: 0 }}>
                      <ListItemText
                        primary={key}
                        secondary={Array.isArray(value) ? value.join(' • ') : String(value)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PlanItemPage;
